import React, { useState, useRef, useEffect } from 'react';
import { MdArrowDropDown, MdArrowDropUp } from 'react-icons/md';
import { useDispatch, useSelector } from 'react-redux';
import { Link, NavLink } from 'react-router-dom';

import { Skeleton } from '@material-ui/lab';

import { IState } from '~/store';
import {
  changeUserPasswordResetRequest,
  getUserProfileRequest,
} from '~/store/modules/users/actions';
import colors from '~/styles/colors';

import Logo from '../../assets/wedo1.svg';
import { useAuth } from '../../hooks/auth';
import Avatar from '../Avatar';
import Modal from '../Modal';
import ChangePasswordForm from './ChangePasswordForm';
import { navItems } from './nav-items';
import {
  Container,
  SubContainer,
  Content,
  LogoContent,
  NavigationMenuContent,
  Nav,
  UserMenuContent,
  UserMenu,
  FirstAccessText,
} from './styles';
import UserDropdown from './UserDropdown';

const Header: React.FC = () => {
  const refTooltip = useRef(null);
  const [showTooltip, setShowTooltip] = useState(false);
  const [modalChangePassword, setModalChangePassword] = useState<boolean>(
    false,
  );

  const [disableCloseModalButton, setDisableCloseModalButton] = useState<
    boolean
  >(false);

  const dispatch = useDispatch();

  const userProfile = useSelector((state: IState) => state.users.userProfile);
  const changePasswordError = useSelector(
    (state: IState) => state.users.changePasswordError,
  );

  const loadingGroupsOrProfile = useSelector(
    (state: IState) => state.users.loadingGroupsOrProfile,
  );

  const { user: authUser, checkPermission } = useAuth();

  const user = {
    name: `${authUser?.firstName} ${authUser?.lastName}`,
    email: authUser?.username,
  };

  const getThumbnail = () => {
    return userProfile?.id === authUser.personId || !userProfile
      ? userProfile?.thumbnail ?? localStorage.getItem('@Afferolab:thumbnail')
      : undefined;
  };

  useEffect(() => {
    const click = (): void => {
      if (showTooltip) {
        setShowTooltip(false);
      }
    };

    document.addEventListener('click', click);
    return () => {
      document.removeEventListener('click', click);
    };
  }, [showTooltip]);

  useEffect(() => {
    const thumbnail = localStorage.getItem('@Afferolab:thumbnail');

    if (!thumbnail) {
      dispatch(getUserProfileRequest(authUser.personId));
    }

    if (authUser.mustChangePassword) {
      setModalChangePassword(true);
      setDisableCloseModalButton(true);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (userProfile?.thumbnail && userProfile?.id === authUser.personId) {
      localStorage.setItem('@Afferolab:thumbnail', userProfile.thumbnail);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userProfile?.thumbnail]);

  useEffect(() => {
    if (changePasswordError === false) {
      setModalChangePassword(false);
      dispatch(changeUserPasswordResetRequest());
    }
  }, [changePasswordError, dispatch]);

  return (
    <Container>
      <SubContainer>
        <Content>
          <LogoContent>
            <Link to="/">
              <img
                src={Logo}
                alt="Logo-affero"
                style={{ width: '84px', height: '32px' }}
              />
            </Link>
          </LogoContent>

          <NavigationMenuContent>
            <Nav>
              {navItems.map(
                ({ label, to, permissions }) =>
                  checkPermission(permissions) && (
                    <li>
                      <NavLink to={to} activeClassName="active">
                        {label}
                      </NavLink>
                    </li>
                  ),
              )}
            </Nav>
          </NavigationMenuContent>

          <UserMenuContent>
            <UserMenu
              role="button"
              tabIndex={0}
              onClick={() => setShowTooltip(!showTooltip)}
            >
              {showTooltip ? (
                <MdArrowDropDown color={colors.primary} size={24} />
              ) : (
                <MdArrowDropUp color={colors.primary} size={24} />
              )}

              {loadingGroupsOrProfile && !getThumbnail() ? (
                <Skeleton
                  animation="wave"
                  variant="rect"
                  style={{
                    borderRadius: '50%',
                    height: '33px',
                    width: '33px',
                  }}
                />
              ) : (
                <Avatar
                  image={getThumbnail() ?? undefined}
                  variant="secondary"
                  size={33}
                />
              )}
            </UserMenu>

            {showTooltip && (
              <UserDropdown
                setOpenModal={setModalChangePassword}
                ref={refTooltip}
                user={user}
              />
            )}
          </UserMenuContent>
        </Content>
      </SubContainer>
      <Modal
        open={modalChangePassword}
        modalState={(opened) => setModalChangePassword(opened)}
        title="Alterar senha"
        disableCloseButton={disableCloseModalButton}
      >
        {authUser.mustChangePassword && (
          <FirstAccessText>
            Nota: Para continuar navegando no Wedo, por favor efetue alteração
            de senha no primeiro acesso!
          </FirstAccessText>
        )}
        <ChangePasswordForm />
      </Modal>
    </Container>
  );
};
export default Header;
